/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/purchase_order_statuses-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-purchase_order_statuses-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/purchase_order_statuses-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, organization_id, is_default, is_active
 * - Sortable fields: created_at, updated_at
 * - Required fields: organization_id, name, code, color
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/purchase_order_statuses-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-purchase_order_statuses-api-client.ts (API client)
 * - client/api/generated_schemas/purchase_order_statuses.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-08-20T18:11:43.993Z
 * Source config: config/entities/purchase_order_statuses-entity-config.ts
 * Public fields: 12/12 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PurchaseOrderStatusesClient, PurchaseOrderStatusesQueryOptions } from '../generated_clients/generated-purchase_order_statuses-api-client.js';
import {
  PurchaseOrderStatuses,
  PurchaseOrderStatusesInsert,
  PurchaseOrderStatusesUpdate,
  PurchaseOrderStatusesFieldConfig
} from '../generated_schemas/generated-purchase_order_statuses-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/purchase_order_statuses_business_hooks/

// Query keys with enhanced caching strategy
export const purchaseOrderStatusesKeys = {
  all: ['purchase_order_statuses'] as const,
  lists: () => [...purchaseOrderStatusesKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...purchaseOrderStatusesKeys.lists(), { filters }] as const,
  details: () => [...purchaseOrderStatusesKeys.all, 'detail'] as const,
  detail: (id: string) => [...purchaseOrderStatusesKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...purchaseOrderStatusesKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...purchaseOrderStatusesKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...purchaseOrderStatusesKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...purchaseOrderStatusesKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const usePurchaseOrderStatusesClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new PurchaseOrderStatusesClient();
};

/**
 * Hook to get all purchase_order_statuses with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with purchase_order_statuses data
 */
export const usePurchaseOrderStatusesList = (options?: PurchaseOrderStatusesQueryOptions) => {
  const client = usePurchaseOrderStatusesClient();

  return useQuery({
    queryKey: purchaseOrderStatusesKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
    // Always reconcile with the server on mount, even when persisted/cached
    // data is restored (field PWA offline cache) or a cross-device write
    // happened elsewhere. Cached data still paints instantly; the refetch
    // corrects it. Offline, the refetch simply fails and the cache is kept.
    // Nobody should ever see stale operational data. (card rKkWHT_XhbXU)
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

/**
 * Hook to get a single purchase_order_statuse by ID
 *
 * @param id - The purchase_order_statuse ID
 * @returns React Query result with single purchase_order_statuse data
 */
export const usePurchaseOrderStatuses = (id: string) => {
  const client = usePurchaseOrderStatusesClient();

  return useQuery({
    queryKey: purchaseOrderStatusesKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
    // Always reconcile with the server on mount so a reload / navigation never
    // paints a stale restored (persisted) detail snapshot. (card rKkWHT_XhbXU)
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/purchase_order_statuses
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/purchase_order_statuses_business_hooks/
 */

/**
 * Hook to create a new purchase_order_statuse
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: organization_id, name, code, color
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating purchase_order_statuse
 */
export const useCreatePurchaseOrderStatuses = () => {
  const client = usePurchaseOrderStatusesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: PurchaseOrderStatusesInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: purchaseOrderStatusesKeys.all });
    },
  });
};

/**
 * Hook to update an existing purchase_order_statuse
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating purchase_order_statuse
 */
export const useUpdatePurchaseOrderStatuses = () => {
  const client = usePurchaseOrderStatusesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PurchaseOrderStatusesUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: purchaseOrderStatusesKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: purchaseOrderStatusesKeys.lists() });
    },
  });
};

/**
 * Hook to delete a purchase_order_statuse
 *
 * @returns React Query mutation for deleting purchase_order_statuse
 */
export const useDeletePurchaseOrderStatuses = () => {
  const client = usePurchaseOrderStatusesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: purchaseOrderStatusesKeys.all });
    },
  });
};

/**
 * Hook to count purchase_order_statuses with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, organization_id, is_default, is_active
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const usePurchaseOrderStatusesCount = (filter?: Partial<Record<typeof PurchaseOrderStatusesFieldConfig.filterable[number], any>>) => {
  const client = usePurchaseOrderStatusesClient();
  
  return useQuery({
    queryKey: purchaseOrderStatusesKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const usePurchaseOrderStatusesFieldConfig = () => {
  return PurchaseOrderStatusesFieldConfig;
};